/**
 * Typeahead agency picker backed by the public members endpoint. Mirrors the
 * original wizard's agency search: type to filter, select to lock in the member.
 */
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '../lib/api';

export interface SelectedAgency {
  name: string;
  memberId: string;
  accountId: string;
}

export function AgencyPicker({
  value,
  onSelect,
  onClear,
}: {
  value: SelectedAgency | null;
  onSelect: (a: SelectedAgency) => void;
  onClear: () => void;
}) {
  const [q, setQ] = useState('');
  const { data } = useQuery({
    queryKey: ['members'],
    queryFn: () => api.listMembers(),
    staleTime: 5 * 60 * 1000,
  });

  const matches = useMemo(() => {
    const members = data?.members ?? [];
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    return members
      .filter((m) => m.name.toLowerCase().includes(needle) || m.member_id.includes(needle))
      .slice(0, 8);
  }, [data, q]);

  if (value) {
    return (
      <div className="fgroup">
        <label className="flabel">Your agency</label>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--r-sm)',
            padding: '10px 14px',
          }}
        >
          <div>
            <div style={{ fontWeight: 600 }}>{value.name}</div>
            <div className="sub-meta">Member ID {value.memberId || '—'}</div>
          </div>
          <button className="reset-link" onClick={onClear}>
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fgroup">
      <label className="flabel">Your agency</label>
      <input
        className="finput"
        placeholder="Start typing your agency name…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        autoComplete="off"
      />
      {matches.length > 0 && (
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-sm)',
            marginTop: 6,
            overflow: 'hidden',
          }}
        >
          {matches.map((m) => (
            <div
              key={m.member_id}
              onClick={() => {
                onSelect({ name: m.name, memberId: m.member_id, accountId: m.account_id ?? '' });
                setQ('');
              }}
              style={{ padding: '9px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
              onMouseDown={(e) => e.preventDefault()}
            >
              <div style={{ fontWeight: 500 }}>{m.name}</div>
              <div className="sub-meta">Member ID {m.member_id}</div>
            </div>
          ))}
        </div>
      )}
      {q.trim() && matches.length === 0 && (
        <div
          style={{
            marginTop: 8,
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-sm)',
            padding: '12px 14px',
            background: 'var(--surface)',
          }}
        >
          <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 10 }}>
            We don't see "<b>{q.trim()}</b>" in the agency directory yet. If your agency isn't
            listed — for example, you're newer or still onboarding — add it as typed and keep going.
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              onSelect({ name: q.trim(), memberId: '', accountId: '' });
              setQ('');
            }}
          >
            Use "{q.trim()}" as my agency
          </button>
        </div>
      )}
    </div>
  );
}
